import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { designComponents } from '../components/LoginDesigns/DesignTemplates';

// Runtime detection - works in both localhost and production
const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api' 
  : '/api';

function LoginPage({ setUser, broker }) {
  const navigate = useNavigate();
  
  // Check URL parameter for mode
  const urlParams = new URLSearchParams(window.location.search);
  const mode = urlParams.get('mode');
  
  const [isLogin, setIsLogin] = useState(mode !== 'register');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password_confirm: '',
    full_name: '',
    tc_no: '',
    phone: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeDesign, setActiveDesign] = useState(null);

  // Use broker from props (passed from App.js)
  const homepageBroker = broker;

  // Design definitions (same as LoginDesignGallery)
  const designs = {
    1: { name: "Modern Minimal", bg: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", primary: "#667eea", secondary: "#764ba2" },
    2: { name: "Ocean Blue", bg: "linear-gradient(135deg, #0093E9 0%, #80D0C7 100%)", primary: "#0093E9", secondary: "#80D0C7" },
    3: { name: "Sunset Orange", bg: "linear-gradient(135deg, #FA8BFF 0%, #2BD2FF 90%, #2BFF88 100%)", primary: "#FA8BFF", secondary: "#2BD2FF" },
    4: { name: "Forest Green", bg: "linear-gradient(135deg, #0F2027 0%, #203A43 0%, #2C5364 100%)", primary: "#2C5364", secondary: "#0F2027" },
    5: { name: "Royal Purple", bg: "linear-gradient(135deg, #8E2DE2 0%, #4A00E0 100%)", primary: "#8E2DE2", secondary: "#4A00E0" },
    6: { name: "Cherry Red", bg: "linear-gradient(135deg, #EB3349 0%, #F45C43 100%)", primary: "#EB3349", secondary: "#F45C43" },
    7: { name: "Mint Fresh", bg: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)", primary: "#11998e", secondary: "#38ef7d" },
    8: { name: "Gold Luxury", bg: "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)", primary: "#f7971e", secondary: "#ffd200" },
    9: { name: "Sky Blue", bg: "linear-gradient(135deg, #00d2ff 0%, #3a7bd5 100%)", primary: "#00d2ff", secondary: "#3a7bd5" },
    10: { name: "Rose Pink", bg: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)", primary: "#f093fb", secondary: "#f5576c" },
    11: { name: "Midnight Black", bg: "linear-gradient(135deg, #000000 0%, #434343 100%)", primary: "#434343", secondary: "#000000" },
    12: { name: "Deep Space", bg: "linear-gradient(135deg, #000428 0%, #004e92 100%)", primary: "#004e92", secondary: "#000428" },
    13: { name: "Carbon Fiber", bg: "linear-gradient(135deg, #232526 0%, #414345 100%)", primary: "#414345", secondary: "#232526" },
    14: { name: "Dark Knight", bg: "linear-gradient(135deg, #141E30 0%, #243B55 100%)", primary: "#243B55", secondary: "#141E30" },
    15: { name: "Obsidian", bg: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)", primary: "#2a5298", secondary: "#1e3c72" },
    16: { name: "Neon Nights", bg: "linear-gradient(135deg, #f857a6 0%, #ff5858 100%)", primary: "#f857a6", secondary: "#ff5858" },
    17: { name: "Cyber Punk", bg: "linear-gradient(135deg, #7F00FF 0%, #E100FF 100%)", primary: "#7F00FF", secondary: "#E100FF" },
    18: { name: "Electric Blue", bg: "linear-gradient(135deg, #0575E6 0%, #021B79 100%)", primary: "#0575E6", secondary: "#021B79" },
    19: { name: "Lime Green", bg: "linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)", primary: "#56ab2f", secondary: "#a8e063" },
    20: { name: "Magenta Dream", bg: "linear-gradient(135deg, #ff0084 0%, #33001b 100%)", primary: "#ff0084", secondary: "#33001b" },
    21: { name: "Soft Lavender", bg: "linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)", primary: "#e0c3fc", secondary: "#8ec5fc" },
    22: { name: "Peach Cream", bg: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)", primary: "#ffecd2", secondary: "#fcb69f" },
    23: { name: "Baby Blue", bg: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)", primary: "#a8edea", secondary: "#fed6e3" },
    24: { name: "Mint Ice", bg: "linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)", primary: "#d299c2", secondary: "#fef9d7" },
    25: { name: "Cotton Candy", bg: "linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)", primary: "#fbc2eb", secondary: "#a6c1ee" },
    26: { name: "Corporate Blue", bg: "linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)", primary: "#2193b0", secondary: "#6dd5ed" },
    27: { name: "Business Gray", bg: "linear-gradient(135deg, #bdc3c7 0%, #2c3e50 100%)", primary: "#bdc3c7", secondary: "#2c3e50" },
    28: { name: "Executive Black", bg: "linear-gradient(135deg, #434343 0%, #000000 100%)", primary: "#434343", secondary: "#000000" },
    29: { name: "Navy Professional", bg: "linear-gradient(135deg, #00467f 0%, #a5cc82 100%)", primary: "#00467f", secondary: "#a5cc82" },
    30: { name: "Teal Corporate", bg: "linear-gradient(135deg, #134E5E 0%, #71B280 100%)", primary: "#134E5E", secondary: "#71B280" },
    31: { name: "Warm Sunset", bg: "linear-gradient(135deg, #ee9ca7 0%, #ffdde1 100%)", primary: "#ee9ca7", secondary: "#ffdde1" },
    32: { name: "Autumn Leaves", bg: "linear-gradient(135deg, #DAA520 0%, #DC143C 100%)", primary: "#DAA520", secondary: "#DC143C" },
    33: { name: "Caramel", bg: "linear-gradient(135deg, #c79081 0%, #dfa579 100%)", primary: "#c79081", secondary: "#dfa579" },
    34: { name: "Copper Glow", bg: "linear-gradient(135deg, #b06ab3 0%, #4568dc 100%)", primary: "#b06ab3", secondary: "#4568dc" },
    35: { name: "Terracotta", bg: "linear-gradient(135deg, #e96443 0%, #904e95 100%)", primary: "#e96443", secondary: "#904e95" },
    36: { name: "Arctic Ice", bg: "linear-gradient(135deg, #74ebd5 0%, #9face6 100%)", primary: "#74ebd5", secondary: "#9face6" },
    37: { name: "Ocean Breeze", bg: "linear-gradient(135deg, #2E3192 0%, #1BFFFF 100%)", primary: "#2E3192", secondary: "#1BFFFF" },
    38: { name: "Glacier Blue", bg: "linear-gradient(135deg, #e0eafc 0%, #cfdef3 100%)", primary: "#e0eafc", secondary: "#cfdef3" },
    39: { name: "Turquoise Sea", bg: "linear-gradient(135deg, #1fa2ff 0%, #12d8fa 50%, #a6ffcb 100%)", primary: "#1fa2ff", secondary: "#a6ffcb" },
    40: { name: "Teal Wave", bg: "linear-gradient(135deg, #00c9ff 0%, #92fe9d 100%)", primary: "#00c9ff", secondary: "#92fe9d" },
    41: { name: "Cosmic Purple", bg: "linear-gradient(135deg, #ff00cc 0%, #333399 100%)", primary: "#ff00cc", secondary: "#333399" },
    42: { name: "Galaxy", bg: "linear-gradient(135deg, #5433FF 0%, #20BDFF 50%, #A5FECB 100%)", primary: "#5433FF", secondary: "#A5FECB" },
    43: { name: "Neon Green", bg: "linear-gradient(135deg, #7ee8fa 0%, #eec0c6 100%)", primary: "#7ee8fa", secondary: "#eec0c6" },
    44: { name: "Tropical", bg: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)", primary: "#fa709a", secondary: "#fee140" },
    45: { name: "Aurora", bg: "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)", primary: "#667eea", secondary: "#f093fb" },
    46: { name: "Fire Red", bg: "linear-gradient(135deg, #f12711 0%, #f5af19 100%)", primary: "#f12711", secondary: "#f5af19" },
    47: { name: "Emerald", bg: "linear-gradient(135deg, #348F50 0%, #56B4D3 100%)", primary: "#348F50", secondary: "#56B4D3" },
    48: { name: "Amethyst", bg: "linear-gradient(135deg, #9D50BB 0%, #6E48AA 100%)", primary: "#9D50BB", secondary: "#6E48AA" },
    49: { name: "Sapphire", bg: "linear-gradient(135deg, #0F2027 0%, #203A43 50%, #2C5364 100%)", primary: "#2C5364", secondary: "#0F2027" },
    50: { name: "Ruby", bg: "linear-gradient(135deg, #C94B4B 0%, #4B134F 100%)", primary: "#C94B4B", secondary: "#4B134F" }
  };

  // Fetch active design ID
  const [activeDesignId, setActiveDesignId] = useState(1);
  
  useEffect(() => {
    const fetchActiveDesign = async () => {
      try {
        const response = await fetch(`${API}/public/active-login-design`);
        const data = await response.json();
        setActiveDesignId(data.design_id || 1);
      } catch (error) {
        console.error('Failed to fetch active design:', error);
        setActiveDesignId(1); // Default
      }
    };
    fetchActiveDesign();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🔵 handleSubmit triggered!', { email: formData.email, isLogin });
    setError('');
    setLoading(true);

    // Kayıt olurken şifre eşleşme kontrolü
    if (!isLogin && formData.password !== formData.password_confirm) {
      setError('Şifreler eşleşmiyor!');
      setLoading(false);
      return;
    }

    // Kayıt olurken zorunlu alanları kontrol et
    if (!isLogin) {
      if (!formData.full_name || !formData.tc_no || !formData.phone || !formData.email || !formData.password) {
        setError('Lütfen tüm alanları doldurun!');
        setLoading(false);
        return;
      }
      // TC Kimlik No 11 haneli olmalı
      if (formData.tc_no.length !== 11) {
        setError('TC Kimlik No 11 haneli olmalıdır!');
        setLoading(false);
        return;
      }
    }

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      console.log('🔵 Calling API:', `${API}${endpoint}`);
      
      // Timeout ekle - 30 saniye
      const response = await axios.post(`${API}${endpoint}`, formData, {
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      console.log('🔵 API Response:', response.data);
      
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      
      // Small delay to ensure state update completes before navigation
      setTimeout(() => {
        // Redirect based on user type (using navigate with replace to prevent back button issues)
        if (response.data.user.is_manager) {
          console.log(' Manager login detected, redirecting to /manager-panel');
          navigate('/manager-panel', { replace: true });
        } else if (response.data.user.is_admin) {
          console.log(' Admin login detected, redirecting to /admin');
          navigate('/admin', { replace: true });
        } else {
          console.log(' User login detected, redirecting to /dashboard');
          navigate('/dashboard', { replace: true });
        }
      }, 100);
    } catch (err) {
      console.log(' Login error:', err.response?.data || err.message || err);
      
      let errorMessage = 'Bir hata oluştu';
      
      // Network/Connection hatası
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Bağlantı zaman aşımına uğradı. Lütfen tekrar deneyin.';
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        errorMessage = 'Sunucuya bağlanılamıyor. İnternet bağlantınızı kontrol edin.';
      } else if (err.response?.status === 503 || err.message?.includes('temporarily unavailable')) {
        errorMessage = 'Sunucu geçici olarak kullanılamıyor. Birkaç saniye sonra tekrar deneyin.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      
      // Check if it's a ban error
      if (err.response?.status === 403 || errorMessage.includes('askıya alınmıştır')) {
        setError('⛔ ' + errorMessage);
      } else {
        setError(errorMessage);
      }
      
      setLoading(false);
    }
  };

  // Render the selected design template
  const DesignComponent = designComponents[activeDesignId] || designComponents[1];
  
  if (DesignComponent) {
    return (
      <DesignComponent
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        loading={loading}
        error={error}
        isLogin={isLogin}
        setIsLogin={setIsLogin}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        broker={homepageBroker}
      />
    );
  }

  // Fallback to default design
  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ 
        background: 'linear-gradient(180deg, #7a0d10 0%, #4a0508 45%, #200205 80%, #0a0203 100%)'
      }}
    >
      {/* Kırmızı Gradient Arka Plan — Soyut Geometrik Şekiller (blurred) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Üstteki üçgen — koyu kırmızı, hafif blur */}
        <div
          className="absolute"
          style={{
            top: '-4%',
            left: '48%',
            width: '160px',
            height: '160px',
            transform: 'translateX(-50%) rotate(180deg)',
            background: '#6a0810',
            clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
            filter: 'blur(24px)',
            opacity: 0.85,
          }}
        />
        {/* Üstteki daha küçük üçgen (fotodaki hafif ikincil parça) */}
        <div
          className="absolute"
          style={{
            top: '2%',
            left: '55%',
            width: '90px',
            height: '90px',
            transform: 'translateX(-50%) rotate(200deg)',
            background: '#7a0d10',
            clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
            filter: 'blur(30px)',
            opacity: 0.6,
          }}
        />
        {/* Sol alt yarım daire */}
        <div
          className="absolute"
          style={{
            bottom: '10%',
            left: '-40px',
            width: '180px',
            height: '180px',
            background: '#2a0306',
            borderRadius: '50%',
            filter: 'blur(28px)',
            opacity: 0.9,
          }}
        />
        {/* Sağ alt yarım daire */}
        <div
          className="absolute"
          style={{
            bottom: '8%',
            right: '5%',
            width: '220px',
            height: '220px',
            background: '#1a0203',
            borderRadius: '50%',
            filter: 'blur(32px)',
            opacity: 0.85,
          }}
        />
        {/* Sağ orta yumuşak ışık — kırmızı vurgu */}
        <div
          className="absolute"
          style={{
            top: '35%',
            right: '15%',
            width: '260px',
            height: '260px',
            background: 'radial-gradient(circle, rgba(160, 20, 25, 0.35) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
        />
        {/* Sol orta yumuşak ışık */}
        <div
          className="absolute"
          style={{
            top: '25%',
            left: '5%',
            width: '200px',
            height: '200px',
            background: 'radial-gradient(circle, rgba(120, 15, 20, 0.3) 0%, transparent 70%)',
            filter: 'blur(50px)',
          }}
        />
        {/* Kart taban gölgesi (fotodaki en alt koyu bant) */}
        <div
          className="absolute bottom-0 left-0 right-0"
          style={{
            height: '25%',
            background: 'linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.65) 100%)',
          }}
        />
      </div>

      <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl w-full max-w-md p-8 relative z-10 border border-gray-200">
        <div className="text-center mb-8">
          <div className="mb-4 flex justify-center">
            {homepageBroker && homepageBroker.logo_url ? (
              <img 
                src={homepageBroker.logo_url}
                alt={homepageBroker.name}
                className="h-20 w-auto object-contain"
              />
            ) : (
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            )}
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {homepageBroker?.name || 'Trading Platform'}
          </h1>
          <p className="text-gray-600">Hoş geldiniz</p>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setIsLogin(true)}
            className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
              isLogin
                ? 'text-white shadow-lg transform scale-105'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            style={isLogin ? { background: activeDesign?.bg || "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" } : {}}
          >
            Giriş Yap
          </button>
          <button
            onClick={() => setIsLogin(false)}
            className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
              !isLogin
                ? 'text-white shadow-lg transform scale-105'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            style={!isLogin ? { background: activeDesign?.bg || "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" } : {}}
          >
            Kayıt Ol
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Ad Soyad</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Adınız Soyadınız"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">TC Kimlik No</label>
                <input
                  type="text"
                  value={formData.tc_no}
                  onChange={(e) => setFormData({ ...formData, tc_no: e.target.value })}
                  required
                  maxLength="11"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="11 haneli TC numaranız"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Telefon</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="5xxxxxxxxx"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="email@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">Şifre</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Şifreniz"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {!isLogin && (
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Şifre Tekrar</label>
              <input
                type="password"
                value={formData.password_confirm}
                onChange={(e) => setFormData({ ...formData, password_confirm: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Şifrenizi tekrar girin"
              />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 text-white font-bold rounded-lg hover:opacity-90 disabled:opacity-50 transition-all transform hover:scale-[1.02]"
            style={{ background: activeDesign?.bg || "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
          >
            {loading ? 'Yükleniyor...' : isLogin ? 'Giriş Yap' : 'Kayıt Ol'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default LoginPage;
